import { z } from 'zod';

/*
 * Предупреждения об обновлении портала (АД-09). Перед каждой выкладкой шаг CI сообщает порталу, какой
 * коммит и какие сервисы выходят и когда; портал за 5 минут показывает всем отсчёт, во время выкладки —
 * «Идёт обновление», после — «Портал обновлён, обновите страницу». Сам портал страницу не перезагружает:
 * несохранённое на странице не пропадает. Администратор компании может отдельно предупредить своих
 * сотрудников о работах.
 */

export const UPDATE_SERVICES = ['api', 'worker', 'web'] as const;
export const UpdateService = z.enum(UPDATE_SERVICES);
export type UpdateService = z.infer<typeof UpdateService>;

/** Сколько минут до выкладки предупреждает CI */
export const UPDATE_NOTICE_MINUTES = 5;
/** Сколько после объявленного времени предупреждение ещё действует, если новая версия так и не вышла */
export const UPDATE_NOTICE_TTL_MINUTES = 30;

/** Строка не длиннее n знаков: длинное обрезается, а не отклоняет предупреждение */
const clipped = (n: number) => z.string().transform((s) => s.slice(0, n));
const upTo = <T extends z.ZodType>(item: T, n: number) => z.array(item).transform((a) => a.slice(0, n));

/** Страница портала, где видно изменение: путь от корня портала и подпись «где это» */
export const UpdateNoteLink = z.object({ path: clipped(300), label: clipped(200) });
export type UpdateNoteLink = z.output<typeof UpdateNoteLink>;

/** Запись «Что нового», которая выходит в этой выкладке (из releases.ts новой версии) */
export const UpdateNoteChange = z.object({
  id: clipped(120).default(''),
  title: clipped(300),
  items: upTo(clipped(800), 12).default([]),
  /** Разделы портала — названиями из новой версии */
  modules: upTo(clipped(80), 12).default([]),
  /** Зачем это сделано — поле why записи «Что нового» */
  why: clipped(800).nullable().default(null),
  /** Описание из коммита, который добавил запись: только для владельцев портала и дежурных */
  details: clipped(800).nullable().default(null),
  /** Коды требований ТЗ */
  codes: upTo(clipped(20), 12).default([]),
  /** Где посмотреть после обновления: поле link записи, а без него — места из реестра требований */
  links: upTo(UpdateNoteLink, 3).default([]),
});
export type UpdateNoteChange = z.output<typeof UpdateNoteChange>;

export const UpdateNoteCommit = z.object({
  sha: z.string().regex(/^[0-9a-f]{7,40}$/),
  subject: clipped(300),
  body: clipped(800).default(''),
  codes: upTo(clipped(20), 12).default([]),
});
export type UpdateNoteCommit = z.output<typeof UpdateNoteCommit>;

/**
 * Что выкладывается и зачем (АД-09): собирает CI (scripts/deploy-notes.mjs) по разнице между работающей версией
 * и новой. Из этого строится письмо о предстоящей выкладке.
 */
export const UpdateNotes = z.object({
  /** С какой версией сравнивалось (работающий сервер), 40 знаков; null — неизвестно */
  base: z
    .string()
    .regex(/^[0-9a-f]{40}$/)
    .nullable()
    .default(null),
  changes: upTo(UpdateNoteChange, 40).default([]),
  /** Сколько записей «Что нового» всего, если список обрезан */
  changesTotal: z.number().int().min(0).default(0),
  /** Коммиты без слияний, которые меняют сервисы, новые первыми */
  commits: upTo(UpdateNoteCommit, 60).default([]),
  commitsTotal: z.number().int().min(0).default(0),
  /** Сколько коммитов меняют только документы: портал они не меняют */
  docsCommits: z.number().int().min(0).default(0),
  /** Изменённые файлы, из-за которых перезапускаются сервер и фоновые задачи (api) и страницы (web) */
  files: z
    .object({ api: upTo(clipped(300), 100).default([]), web: upTo(clipped(300), 100).default([]) })
    .default({ api: [], web: [] }),
  filesTotal: z
    .object({ api: z.number().int().min(0).default(0), web: z.number().int().min(0).default(0) })
    .default({ api: 0, web: 0 }),
  /** Новые миграции базы — имена файлов без .sql */
  migrations: upTo(clipped(200), 20).default([]),
  /** Тексты требований ТЗ по кодам из записей и коммитов */
  requirements: upTo(z.object({ code: clipped(20), text: clipped(300) }), 60).default([]),
});
export type UpdateNotes = z.output<typeof UpdateNotes>;

/** Шаг CI: выкладка начнётся через minutes минут */
export const UpdateAnnounceInput = z.object({
  services: z.array(UpdateService).min(1).max(3),
  minutes: z.number().int().min(1).max(30).default(UPDATE_NOTICE_MINUTES),
  /**
   * Что выкладывается и зачем (UpdateNotes). Проверяется отдельно: сведения с ошибкой отбрасываются, а
   * предупреждение всё равно уходит.
   */
  notes: z.unknown().optional(),
});
export type UpdateAnnounceInput = z.infer<typeof UpdateAnnounceInput>;

export const UpdateAnnounceResult = z.object({ id: z.string(), startsAt: z.string() });
export type UpdateAnnounceResult = z.infer<typeof UpdateAnnounceResult>;

/** Прогон CI отменён: сколько предупреждений этого запуска снято */
export const UpdateCancelResult = z.object({ cancelled: z.number().int() });
export type UpdateCancelResult = z.infer<typeof UpdateCancelResult>;

/** Выкладка новой версии из CI */
export const ReleaseNotice = z.object({
  id: z.string(),
  startsAt: z.string(),
  /** Первые 7 знаков коммита, который выкладывается */
  commit: z.string(),
  services: z.array(UpdateService),
  /** Выкладка началась: новая версия API вот-вот запустится (отметку ставит мигратор); null — ещё сборка */
  deployingAt: z.string().nullable(),
});
export type ReleaseNotice = z.infer<typeof ReleaseNotice>;

/** Работы на портале — предупреждение администратора компании своим сотрудникам */
export const MaintenanceNotice = z.object({
  startsAt: z.string(),
  endsAt: z.string(),
  message: z.string().nullable(),
  createdByName: z.string().nullable(),
});
export type MaintenanceNotice = z.infer<typeof MaintenanceNotice>;

export const UpdateStatus = z.object({
  /** Первые 7 знаков коммита работающего API */
  commit: z.string(),
  /** Время сервера — отсчёт в браузере идёт по нему, а не по часам компьютера */
  serverTime: z.string(),
  /** Запуск процесса API; старые серверы могут ещё не передавать это поле. */
  apiStartedAt: z.string().optional(),
  release: ReleaseNotice.nullable(),
  /** Только после входа: предупреждение администратора компании */
  maintenance: MaintenanceNotice.nullable(),
});
export type UpdateStatus = z.infer<typeof UpdateStatus>;

export const MaintenanceInput = z.object({
  /** Через сколько минут начнутся работы (0 — уже идут) */
  inMinutes: z.number().int().min(0).max(1440),
  /** Сколько продлятся работы, минут */
  durationMinutes: z.number().int().min(5).max(480),
  message: z.string().trim().max(200, 'Не длиннее 200 знаков').optional(),
});
export type MaintenanceInput = z.infer<typeof MaintenanceInput>;

/** Для «Администрирование → Настройки»: последнее предупреждение CI и работы компании */
export const UpdateNoticesAdmin = z.object({
  release: ReleaseNotice.extend({ createdAt: z.string(), runId: z.string().nullable() }).nullable(),
  maintenance: MaintenanceNotice.nullable(),
  commit: z.string(),
});
export type UpdateNoticesAdmin = z.infer<typeof UpdateNoticesAdmin>;
